"use client"

import { useDictionary } from "@/core/hooks/use-dictionary";
import { Control, Controller, FieldErrors, useFieldArray, UseFormRegister } from "react-hook-form";
import { MultilingualInput, MultilingualTextareaInput } from "../GlobalWidgetEditor";
import { Heading } from "@/components/ui/Heading";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import FileUploadInput from "../../input/MultipleFileUploadInput";
import LinkSelector from "../../LinkSelector";
import { Label } from "@/components/ui/label";

interface ScrollGalleryWidgetProps {
    widgetIndex: number;
    name: string;
    control: Control<any>;
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    disabled?: boolean;
}

export const ScrollGalleryWidget = ({
    widgetIndex,
    name,
    control,
    register,
    errors,
    disabled,
}: ScrollGalleryWidgetProps) => {
    const { dictionary } = useDictionary();

    const { fields, append, remove } = useFieldArray({
        control,
        name: `${name}.${widgetIndex}.items`,
    });

    const getError = (path: string) => {
        const errorPath = path.split('.');
        let current = errors;
        for (const key of errorPath) {
            if (current && typeof key === 'string' && key in current) {
                current = (current as any)[key];
            } else {
                return undefined;
            }
        }
        return (current as any)?.message;
    };

    const addItem = () => {
        append({
            image: undefined,
            width: '1/2',
            orientation: 'vertical',
            title: '',
            title_en: '',
            title_ar: '',
            description: '',
            description_en: '',
            description_ar: '',
            link: '',
        });
    };

    const removeItem = (itemIndex: number) => {
        if (fields.length <= 1) return;
        remove(itemIndex);
    };

    return (
        <>
            <MultilingualInput
                baseName={`${name}.${widgetIndex}.title`}
                label={dictionary.forms?.title}
                register={register}
                errors={errors}
                disabled={disabled}
            />

            <MultilingualTextareaInput
                baseName={`${name}.${widgetIndex}.description`}
                label={dictionary.forms?.description}
                register={register}
                errors={errors}
                disabled={disabled}
            />

            {fields.map((field, itemIndex) => {
                const imageError = getError(`${name}.${widgetIndex}.items.${itemIndex}.image`) as string | undefined;

                return (
                    <div key={field.id} className="border col-span-2 p-4 rounded-md space-y-4 bg-background">
                        <div className="flex justify-between items-center">
                            <Heading level={4}>
                                کارت {itemIndex + 1}
                            </Heading>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeItem(itemIndex)}
                                disabled={fields.length <= 1 || disabled}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-muted-foreground">
                                    {dictionary.forms?.width || 'عرض کارت'}
                                </label>
                                <Controller
                                    control={control}
                                    name={`${name}.${widgetIndex}.items.${itemIndex}.width`}
                                    render={({ field: widthField }) => (
                                        <Select
                                            value={widthField.value || '1/2'}
                                            onValueChange={widthField.onChange}
                                            disabled={disabled}
                                        >
                                            <SelectTrigger className="w-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="1/1">1/1</SelectItem>
                                                <SelectItem value="3/4">3/4</SelectItem>
                                                <SelectItem value="2/3">2/3</SelectItem>
                                                <SelectItem value="1/2">1/2</SelectItem>
                                                <SelectItem value="1/3">1/3</SelectItem>
                                                <SelectItem value="1/4">1/4</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-medium text-muted-foreground">
                                    {dictionary.forms?.orientation || 'جهت تصویر'}
                                </label>
                                <Controller
                                    control={control}
                                    name={`${name}.${widgetIndex}.items.${itemIndex}.orientation`}
                                    render={({ field: orientationField }) => (
                                        <Select
                                            value={orientationField.value || 'vertical'}
                                            onValueChange={orientationField.onChange}
                                            disabled={disabled}
                                        >
                                            <SelectTrigger className="w-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="vertical">{dictionary.forms?.vertical || 'عمودی'}</SelectItem>
                                                <SelectItem value="horizontal">{dictionary.forms?.horizontal || 'افقی'}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs text-primary-600">تصویر یا ویدیو (عمودی ۳:۴ یا افقی ۱۶:۹، بسته به جهت انتخاب‌شده)</label>
                            <Controller
                                control={control}
                                name={`${name}.${widgetIndex}.items.${itemIndex}.image`}
                                render={({ field }) => (
                                    <FileUploadInput
                                        accept="image/*,video/*"
                                        disabled={disabled}
                                        onUploadSuccess={(image) => {
                                            if (!Array.isArray(image) && image) field.onChange(image);
                                        }}
                                        defaultImages={
                                            field.value
                                                ? [{ ...field.value, name: field.value.name || '', description: field.value.description || '' }]
                                                : []
                                        }
                                        onDefaultRemove={() => field.onChange(undefined)}
                                        error={imageError}
                                    />
                                )}
                            />
                        </div>

                        <MultilingualInput
                            baseName={`${name}.${widgetIndex}.items.${itemIndex}.title`}
                            label={dictionary.forms?.title}
                            register={register}
                            errors={errors}
                            disabled={disabled}
                        />

                        <MultilingualTextareaInput
                            baseName={`${name}.${widgetIndex}.items.${itemIndex}.description`}
                            label={dictionary.forms?.description}
                            register={register}
                            errors={errors}
                            disabled={disabled}
                        />

                        <div className="space-y-1">
                            <Label className="text-xs font-medium text-muted-foreground">
                                {dictionary.forms?.link}
                            </Label>
                            <Controller
                                control={control}
                                name={`${name}.${widgetIndex}.items.${itemIndex}.link`}
                                render={({ field: linkField }) => (
                                    <LinkSelector
                                        value={linkField.value || ''}
                                        onChange={linkField.onChange}
                                        placeholder={dictionary.forms?.link}
                                    />
                                )}
                            />
                        </div>
                    </div>
                );
            })}

            <Button type="button" variant="outline" onClick={addItem} disabled={disabled} className="mt-2">
                <Plus className="h-4 w-4 mr-2" />
                {dictionary.common.add + " " + dictionary.forms.item}
            </Button>
        </>
    );
};